"use client";

import { useEffect, useRef, useState } from "react";

import { ArrowRight, ChevronDown } from "lucide-react";
import { BLOG_PREVIEW_POST_ID } from "@/app/about/page";
import BlogPreviewTile from "../BlogPreviewTile";
import { Button } from "../ui/button";
import Image from "next/image";
import Link from "next/link";
import RegisterDownloadButton from "../RegisterDownloadButton";
import { WPRecentPost } from "@/types";

function getScrollContainer(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return null;
}

export default function HomePage({
  recentPosts,
}: {
  recentPosts: WPRecentPost[];
}) {
  const [showBlog, setShowBlog] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const blogRef = useRef<HTMLDivElement>(null);
  const pendingScroll = useRef(false);
  const prevScrollTop = useRef(0);

  // Scroll to blog section after it mounts
  useEffect(() => {
    if (!showBlog || !pendingScroll.current || !blogRef.current) return;
    pendingScroll.current = false;

    const container = getScrollContainer(blogRef.current);
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const blogRect = blogRef.current.getBoundingClientRect();
    const scrollTarget = container.scrollTop + (blogRect.top - containerRect.top);
    container.scrollTo({ top: scrollTarget, behavior: "smooth" });
  }, [showBlog]);

  // Re-hide blog when scrolling back up past 50% of the hero
  useEffect(() => {
    if (!showBlog || !heroRef.current) return;

    const container = getScrollContainer(heroRef.current);
    if (!container) return;

    // Seed prevScrollTop from the container's actual position so the first
    // scroll event after re-opening doesn't look like an upward move.
    prevScrollTop.current = container.scrollTop;
    const heroHeight = heroRef.current.clientHeight;
    let hiding = false;

    const handleScroll = () => {
      if (hiding) return;
      const scrollTop = container.scrollTop;
      const isUp = scrollTop < prevScrollTop.current;
      prevScrollTop.current = scrollTop;

      if (isUp && scrollTop < heroHeight * 0.5) {
        hiding = true;
        // Smooth back to top first, then unmount so there's no abrupt jump
        container.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => setShowBlog(false), 380);
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [showBlog]);

  const handleReadMore = () => {
    pendingScroll.current = true;
    setShowBlog(true);
  };

  const featuredPost = recentPosts.find(
    (x) => x.node.id === BLOG_PREVIEW_POST_ID,
  );

  return (
    <div>
      {/* Hero */}
      <div
        ref={heroRef}
        className="h-[calc(100dvh-56px)] md:h-[calc(100dvh-80px)] flex flex-col md:py-3"
      >
        {/* Mobile: full-height gif panning left→right */}
        <div
          className="md:hidden relative flex-1 min-h-0 w-full gif-pan-mobile"
          style={{ backgroundImage: "url('/gif/stone_guide.gif')" }}
        >
          <div className="backdrop-blur-sm absolute inset-0">
            <div className="absolute flex-1 min-h-0 w-full z-10 inset-0 top-1/2 -translate-y-1/2">
              <Image
                src="/gif/stone_guide.gif"
                className="object-contain"
                fill
                alt="stone"
                unoptimized
              />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-background to-transparent pointer-events-none" />
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <Button variant={"secondary"} asChild>
              <Link href="/about">About Us</Link>
            </Button>
          </div>
        </div>

        {/* Desktop: contained gif + bottom bar */}
        <div className="hidden md:flex items-center gap-4 justify-between flex-col flex-1 min-h-0">
          <div className="relative flex-1 min-h-0 w-full">
            <Image
              src="/gif/stone_guide.gif"
              className="object-contain"
              fill
              alt="stone"
              unoptimized
            />
          </div>
          <div>
            <RegisterDownloadButton />
          </div>
          <div className="flex items-center justify-end pt-3 shrink-0 px-1">
            <button
              onClick={handleReadMore}
              className="flex items-center gap-2 text-sm text-foreground/50 hover:text-foreground transition-colors duration-200 cursor-pointer"
            >
              Read More
              <ArrowRight className="w-3.5 h-3.5" />
              <ChevronDown className="w-3.5 h-3.5 animate-pulse" />
            </button>
          </div>
        </div>
      </div>

      {/* Blog preview — shown after "Read More", hidden when scrolled back */}
      {featuredPost && showBlog && (
        <div
          ref={blogRef}
          className="hidden md:flex w-full border-t border-foreground/10 px-4 sm:px-6 lg:px-8 h-screen items-center"
        >
          <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[0.35fr_0.65fr] lg:items-start">
            <h2 className="font-PlayfairDisplay text-3xl font-semibold md:text-4xl">
              From the Blog
            </h2>
            <BlogPreviewTile
              post={featuredPost}
              excerpt={featuredPost.node.excerpt}
              animationTrigger={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
